/**
 * Shared attendance presentation — stat cards + day-by-day timeline.
 * Used by MyAttendance (own) and hr/EmployeeAttendance (HR picks a staff member).
 * Both pages feed it the same { summary, days } shape from the attendance API.
 */
import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import type { MyAttendanceDay, MyAttendanceResp } from '../../hooks';
import { resolveFileUrl } from '../../lib/api-client';

const KIND_LABEL: Record<string, string> = {
  morning_in: 'เข้า',
  lunch_out: 'พัก',
  lunch_in: 'กลับ',
  evening_out: 'เลิก',
};
const KIND_ORDER = ['morning_in', 'lunch_out', 'lunch_in', 'evening_out'] as const;

export function thisMonthRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const yyyy = bkk.getUTCFullYear();
  const mm = String(bkk.getUTCMonth() + 1).padStart(2, '0');
  const today = `${yyyy}-${mm}-${String(bkk.getUTCDate()).padStart(2, '0')}`;
  return { from: `${yyyy}-${mm}-01`, to: today };
}

export function lastMonthRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const y = bkk.getUTCFullYear();
  const m = bkk.getUTCMonth();
  const lastY = m === 0 ? y - 1 : y;
  const lastM = m === 0 ? 12 : m;
  const mm = String(lastM).padStart(2, '0');
  const lastDay = new Date(Date.UTC(lastY, lastM, 0)).getUTCDate();
  return { from: `${lastY}-${mm}-01`, to: `${lastY}-${mm}-${String(lastDay).padStart(2, '0')}` };
}

export function last7DaysRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const to = bkk.toISOString().slice(0, 10);
  const from = new Date(bkk.getTime() - 6 * 86400_000).toISOString().slice(0, 10);
  return { from, to };
}

export function last30DaysRange() {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  const to = bkk.toISOString().slice(0, 10);
  const from = new Date(bkk.getTime() - 29 * 86400_000).toISOString().slice(0, 10);
  return { from, to };
}

export type RangePreset = 'this-month' | 'last-month' | 'last-7';
export function rangeForPreset(p: RangePreset) {
  return p === 'this-month' ? thisMonthRange() : p === 'last-month' ? lastMonthRange() : last7DaysRange();
}

export function AttendanceRangePresets({ preset, onChange }: { preset: RangePreset; onChange: (p: RangePreset) => void }) {
  return (
    <div className="flex gap-1 mb-3 overflow-x-auto">
      {([
        { id: 'this-month', label: 'เดือนนี้' },
        { id: 'last-month', label: 'เดือนที่แล้ว' },
        { id: 'last-7', label: '7 วันล่าสุด' },
      ] as const).map((p) => (
        <button
          key={p.id}
          onClick={() => onChange(p.id)}
          className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
            preset === p.id ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-white border border-gray-200 text-gray-600'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function formatThaiDate(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Bangkok',
  }).format(new Date(`${iso}T00:00:00+07:00`));
}

// captured_at เป็น UTC ISO → ต้องแปลงเป็นเวลาไทยด้วย Intl (slice ตรงๆ จะเพี้ยน -7 ชม.)
function formatBkkTime(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Bangkok',
  }).format(new Date(iso));
}

function StatusBadge({ status }: { status: MyAttendanceDay['status'] }) {
  const map: Record<MyAttendanceDay['status'], { label: string; cls: string }> = {
    working: { label: 'ทำงาน', cls: 'bg-emerald-100 text-emerald-700' },
    short_work: { label: 'งานยังไม่ครบ', cls: 'bg-amber-100 text-amber-700' },
    on_leave: { label: 'ลา', cls: 'bg-blue-100 text-blue-700' },
    absent: { label: 'ขาด', cls: 'bg-red-100 text-red-700' },
    holiday: { label: 'หยุด', cls: 'bg-gray-100 text-gray-600' },
  };
  const m = map[status];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

function DayRow({ day }: { day: MyAttendanceDay }) {
  const hasEvents = Object.keys(day.events).length > 0;
  const [expandedKind, setExpandedKind] = useState<string | null>(null);
  const selfiesAvailable = KIND_ORDER.filter((k) => day.events[k]?.selfie_path);

  return (
    <div className="card p-3">
      <div className="flex justify-between items-start mb-1">
        <span className="font-medium text-sm">{formatThaiDate(day.date)}</span>
        <StatusBadge status={day.status} />
      </div>
      {day.shift_code && (
        <p className="text-xs text-gray-500 mb-2">
          {day.shift_code} ({day.shift_start}-{day.shift_end})
        </p>
      )}
      {day.holiday && <p className="text-xs text-gray-500 mb-1">วันหยุด: {day.holiday}</p>}
      {day.leave && <p className="text-xs text-blue-700 mb-1">ลา {day.leave.type}: {day.leave.reason ?? '-'}</p>}
      {hasEvents && (
        <div className="flex gap-3 text-xs text-gray-700">
          {KIND_ORDER.map((k) => {
            const ev = day.events[k];
            if (!ev) return <span key={k} className="text-gray-300">{KIND_LABEL[k]} —</span>;
            const late = ev.late_minutes > 0;
            const hasPic = !!ev.selfie_path;
            return (
              <button
                key={k}
                type="button"
                onClick={() => hasPic && setExpandedKind(expandedKind === k ? null : k)}
                disabled={!hasPic}
                className={`${late ? 'text-amber-700' : 'text-emerald-700'} ${hasPic ? 'underline decoration-dotted' : ''}`}
              >
                {KIND_LABEL[k]} {formatBkkTime(ev.at)}
                {late ? ` (+${ev.late_minutes}น.)` : ''}
              </button>
            );
          })}
        </div>
      )}
      {expandedKind && day.events[expandedKind]?.selfie_path && (
        <div className="mt-2">
          <img
            src={resolveFileUrl(day.events[expandedKind].selfie_path!) ?? ''}
            alt={`${KIND_LABEL[expandedKind]} ${day.date}`}
            className="w-full max-w-xs rounded-lg border border-gray-200"
            loading="lazy"
          />
        </div>
      )}
      {hasEvents && !expandedKind && selfiesAvailable.length > 0 && (
        <p className="text-[10px] text-gray-400 mt-1">📷 แตะที่เวลาเพื่อดูรูป</p>
      )}
    </div>
  );
}

export function AttendanceTimeline({ data, isLoading }: { data: MyAttendanceResp | undefined; isLoading: boolean }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500">วันทำงาน</p>
          <p className="text-xl font-bold text-emerald-700">{data?.summary.working_days ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">วันลา</p>
          <p className="text-xl font-bold text-blue-700">{data?.summary.leave_days ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">วันขาด</p>
          <p className="text-xl font-bold text-red-700">{data?.summary.absent_days ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">สาย (นาที)</p>
          <p className="text-xl font-bold text-amber-700">{data?.summary.total_late_minutes ?? '-'}</p>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-3">
              <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
            </div>
          ))}
        </div>
      )}
      {!isLoading && data && (
        <div className="space-y-2">
          {data.days.length === 0 ? (
            <div className="card p-6 text-center text-muted">
              <CalendarDays className="w-12 h-12 mx-auto mb-2 text-cream" />
              <p className="text-sm">ไม่มีข้อมูลในช่วงนี้</p>
            </div>
          ) : (
            [...data.days].reverse().map((d) => <DayRow key={d.date} day={d} />)
          )}
        </div>
      )}
    </>
  );
}
